import React, { useState, useRef, useEffect } from "react";

interface PictureUploadProps {
  label: string;
  value: string | null | undefined;
  onChange: (base64: string) => void;
  onRemove?: () => void;
  previewSize?: "sm" | "md" | "lg";
  disabled?: boolean;
  currentImage?: string | null | undefined;
}

export const PictureUpload: React.FC<PictureUploadProps> = ({
  label,
  value,
  onChange,
  onRemove,
  previewSize = "md",
  disabled = false,
  currentImage,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(
    value || currentImage || null
  );

  // Update preview when value or currentImage changes
  useEffect(() => {
    setPreview(value || currentImage || null);
  }, [value, currentImage]);

  const compressImage = (file: File, maxWidth: number = 800, maxHeight: number = 800, quality: number = 0.8): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Calculate new dimensions
          let width = img.width;
          let height = img.height;

          if (width > maxWidth || height > maxHeight) {
            if (width > height) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            } else {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }

          // Create canvas and compress
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");

          if (!ctx) {
            reject(new Error("Não foi possível criar o contexto do canvas"));
            return;
          }

          ctx.drawImage(img, 0, 0, width, height);

          // Convert to base64 with compression
          const compressedBase64 = canvas.toDataURL("image/jpeg", quality);
          resolve(compressedBase64);
        };
        img.onerror = () => {
          reject(new Error("Erro ao carregar a imagem"));
        };
        img.src = e.target?.result as string;
      };
      reader.onerror = () => {
        reject(new Error("Erro ao ler o arquivo"));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Por favor, selecione um arquivo de imagem válido");
      return;
    }

    // Validate file size (max 10MB before compression)
    if (file.size > 10 * 1024 * 1024) {
      alert("A imagem deve ter no máximo 10MB");
      return;
    }

    try {
      // Compress and resize image
      const compressedBase64 = await compressImage(file, 800, 800, 0.8);
      
      // Check compressed size (should be much smaller)
      const compressedSize = (compressedBase64.length * 3) / 4; // Approximate base64 size
      if (compressedSize > 5 * 1024 * 1024) {
        // If still too large, compress more aggressively
        const moreCompressed = await compressImage(file, 600, 600, 0.7);
        setPreview(moreCompressed);
        onChange(moreCompressed);
      } else {
        setPreview(compressedBase64);
        onChange(compressedBase64);
      }
    } catch (error) {
      console.error("Error compressing image:", error);
      alert("Erro ao processar a imagem. Por favor, tente novamente.");
    }
  };

  const handleRemove = () => {
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (onRemove) {
      onRemove();
    } else {
      onChange("");
    }
  };

  const sizeClasses = {
    sm: "w-16 h-16",
    md: "w-24 h-24",
    lg: "w-32 h-32",
  };

  const inputId = `picture-upload-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">
        {label}
      </label>
      <div className="flex items-start gap-4">
        {preview && (
          <div className="relative">
            <img
              src={preview}
              alt="Preview"
              className={`${sizeClasses[previewSize]} object-cover rounded-lg border-2 border-gray-200 dark:border-slate-600`}
            />
            <button
              type="button"
              onClick={handleRemove}
              disabled={disabled}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Remover imagem"
            >
              ×
            </button>
          </div>
        )}
        <div className="flex-1">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
            id={inputId}
            disabled={disabled}
          />
          <label
            htmlFor={inputId}
            className={`inline-flex items-center px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-slate-200 bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer transition-colors ${
              disabled ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <span className="mr-2">📷</span>
            {preview ? "Alterar Imagem" : "Selecionar Imagem"}
          </label>
          <p className="mt-2 text-xs text-gray-500 dark:text-slate-400">
            Formatos aceitos: JPG, PNG, GIF. Tamanho máximo: 5MB
          </p>
        </div>
      </div>
    </div>
  );
};
